import React, { useState } from "react";
import toast from "react-hot-toast";
import { PhaseStatus } from "./PhaseStatusPill";
import { markProgramReady as apiMarkProgramReady } from "../../utils/api/endpoints/programs/pretesting";

interface MarkReadyButtonProps {
  programId: number;
  currentPhase?: PhaseStatus;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
}

const MarkReadyButton: React.FC<MarkReadyButtonProps> = ({
  programId,
  currentPhase = PhaseStatus.ACTIVE_REPORTING,
  onSuccess,
  onError,
  className = ""
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleMarkReady = async () => {
    // Input validation
    if (!programId || programId <= 0) {
      const msg = "Invalid program ID";
      toast.error(msg, { position: "bottom-right" });
      onError?.(msg);
      return;
    }

    setIsLoading(true);
    try {
      const data = await apiMarkProgramReady(programId);
      toast.success(
        data?.message || "Program moved to reporting phase",
        { position: "bottom-right" }
      );
      onSuccess?.();
      setShowConfirmation(false);
      console.log("Program successfully marked as ready:", data);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        (error instanceof Error ? error.message : "An error occurred");
      console.error("Error marking program as ready:", error);
      toast.error(errorMessage, { position: "bottom-right" });
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show button if not in pretesting phase
  if (currentPhase !== PhaseStatus.PRETESTING) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setShowConfirmation(true)}
        disabled={isLoading}
        className={`bg-[#58CC02] hover:opacity-90 disabled:opacity-50 text-[#010D2C] font-normal text-sm py-3 px-5 rounded-lg transition-colors duration-200 ${className}`}
      >
        {isLoading ? "Processing..." : "Mark Ready for Reporting"}
      </button>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirm Phase Transition
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to mark this program as ready for the reporting phase? 
              This action will transition the program from pretesting to active reporting.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmation(false)}
                disabled={isLoading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkReady}
                disabled={isLoading}
                className="bg-[#58CC02] hover:opacity-90 disabled:opacity-50 text-[#010D2C] font-normal text-sm py-3 px-5 rounded-lg"
              >
                {isLoading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarkReadyButton;
